import { useMemo, useState } from "react";
import { Box } from "@mui/material";
import { ParagraphText } from "../../../../Libraries";
import TabAutoComplete from "../../../../Libraries/TabAutoComplete";
import { analytics, triggerLogEvent } from "../../../../config/firebase";
import SelectItemModal from "../../../common/Modals/SelectItemModal";
import AddCustomerModal from "../../../common/Modals/AddCustomerModal";
import { phoneNumRegex } from "../../../../validations";
import { useSnackbar } from "../../../../context/SnackbarContext";
import { formatNum } from "../../../../oscar-pos-core/constants";
import formStyles from "../../SaleExpense/SaleExpenseForm/styles.module.css";
import bpStyles from "../BillPayments/BillPayments.module.css";
import { VoucherPlaceholderIcon } from "../../../../assets/icons/icon";
import { sentenceCase } from "../../../../constants";
import styles from "./SellVouchers.module.css"

const VouchersPaymentForm = ({
  I18n,
  popularVouchers = [],
  popularVouchersLoading = false,
  availableVouchers = [],
  state,
  setState,
  setCustomer,
  customers = [],
  navigate,
}) => {
  const { showSnackbar } = useSnackbar();
  const [showAllVouchersModal, setShowAllVouchersModal] = useState(false);
  const [createCustomerModal, setCreateCustomerModal] = useState(false);
  const [initName, setInitname] = useState("");
  const [initPhone, setInitphone] = useState("");
  const [contactToUpdate, setContactToUpdate] = useState(null);
  
  const [failedIcons, setFailedIcons] = useState({});
  
  const markIconFailed = (key) =>
    setFailedIcons((prev) => (prev[key] ? prev : { ...prev, [key]: true }));

  const displayedPopular = (popularVouchers || []).slice(0, 3);
  const showPopularSkeleton = popularVouchersLoading && popularVouchers.length === 0;
  const showPopularSection = showPopularSkeleton || displayedPopular.length > 0;

  // Flat list of ALL individual vouchers across all companies for the "See more" modal
  const allVouchersFlat = useMemo(
    () =>
      (availableVouchers || []).flatMap((company) =>
        (company.vouchers || []).map((v) => {
          const iconSrc = v.vendor_icon || v.icon?.[0] || v.icon || "";
          const iconKey = `modal_${v.voucher_id}`;
          return {
            label: `${v.voucher_type || ""}`.trim(),
            img:
              iconSrc && !failedIcons[iconKey] ? (
                <img
                  src={iconSrc}
                  alt={v.voucher_type || ""}
                  width={28}
                  height={28}
                  style={{ objectFit: "contain" }}
                  onError={() => markIconFailed(iconKey)}
                />
              ) : (
                <VoucherPlaceholderIcon width={19} height={19} />
              ),
            icon: v.icon?.[0] || v.icon || "",
            id: v.voucher_id,
            vendor_name: company.vendor_name,
            voucherObj: v,
          };
        }),
      ),
    [availableVouchers, failedIcons],
  );


  // Company options for the "Voucher company" TabAutoComplete
  const companyOptions = useMemo(
    () =>
      availableVouchers.map((v) => {
        const iconKey = `company_${v.vendor_name}`;
        return {
          label: v.vendor_name || "",
          value: (v.vendor_name || "").trim(),
          name: v.vendor_name || "",
          img:
            v.icon && !failedIcons[iconKey] ? (
              <img
                src={v.icon}
                alt={v.vendor_name}
                width={20}
                height={20}
                onError={() => markIconFailed(iconKey)}
              />
            ) : (
              
              <Box display="flex" alignItems="center" justifyContent="center" width="100%" height="100%">
                <VoucherPlaceholderIcon width={14} height={14} />
              </Box>
            ),
          icon: v.icon || "",
          id: v.vendor_name || "",
        };
      }),
    [availableVouchers, failedIcons],
  );

  // Voucher options for the selected company
  const voucherOptions = useMemo(() => {
    if (!state.voucherCompany) return [];
    const found = availableVouchers.find((v) => v.vendor_name === state.voucherCompany);
    return (found?.vouchers || []).map((v) => {
      const effectivePrice = v.discounted_price ?? v.price;
      const iconSrc = v?.vendor_icon || v.icon?.[0] || v.icon || "";
      const iconKey = `dropdown_${v.voucher_id}`;
      return {
        label: effectivePrice
          ? `${v.voucher_type || ""} (Rs.${formatNum(effectivePrice)})`
          : (v.voucher_type || ""),
        value: (v.voucher_type || "").trim(),
        name: v.voucher_type || "",
        id: v.voucher_id,
        voucherObj: v,
        img:
          iconSrc && !failedIcons[iconKey] ? (
            <img
              src={iconSrc}
              alt={v.voucher_type || ""}
              width={25}
              height={25}
              style={{ objectFit: "contain" }}
              onError={() => markIconFailed(iconKey)}
            />
          ) : (
            <Box display="flex" alignItems="center" justifyContent="center" width="100%" height="100%">
              <VoucherPlaceholderIcon width={17} height={17} />
            </Box>
          ),
      };
    });
  }, [availableVouchers, state.voucherCompany, failedIcons]);

  const contactList = useMemo(
    () =>
      (customers || []).map((item) => ({
        id: item.id,
        labelText: item.name,
        label: (
          <ParagraphText className={formStyles.contactLabel}>
            <span className="newFontColor">{item.name}</span>
            <span className="newLightColor">
              {item?.is_supplier ? I18n.supplier : I18n.customer}
            </span>
          </ParagraphText>
        ),
        value: item.name,
      })),
    [customers, I18n],
  );

  // Clicking a popular card sets both company and the specific voucher
  const handlePopularVoucherSelect = (popularItem) => {
    const displayName = popularItem.name || popularItem.voucher_type || "";
    const vendorName = popularItem.vendor_name || "";
    if (vendorName === state.voucherCompany && popularItem.voucher_id === state.voucherId) return;

    const foundCompany = availableVouchers.find(
      (v) => v.vendor_name?.toLowerCase() === vendorName.toLowerCase(),
    );
    if (!foundCompany) return;

    const foundVoucher = (foundCompany.vouchers || []).find(
      (v) =>
        v.voucher_id === popularItem.voucher_id ||
        (v.voucher_type)?.toLowerCase() === displayName.toLowerCase(),
    );

    setState((e) => ({
      ...e,
      voucherCompany: foundCompany.vendor_name,
      voucherCompanyId: foundCompany.vendor_id || foundCompany.id || vendorName,
      voucherId: foundVoucher?.voucher_id || popularItem.voucher_id || "",
      voucherObj: foundVoucher || null,
    }));
  };

  // Selecting a voucher from the "See more" modal sets both company and voucher
  const handleModalVoucherSelect = (item) => {
    if (item.id === state.voucherId && item.vendor_name === state.voucherCompany) return;
    triggerLogEvent(analytics, "w_earn_vouchers_popular_select");
    const foundCompany = availableVouchers.find((v) => v.vendor_name === item.vendor_name);
    setState((e) => ({
      ...e,
      voucherCompany: item.vendor_name || "",
      voucherCompanyId: foundCompany?.vendor_id || foundCompany?.id || item.vendor_name || "",
      voucherId: item.id || "",
      voucherObj: item.voucherObj || null,
    }));
    setShowAllVouchersModal(false);
  };

  const handleCreateContact = (value) => {
    if (value?.match?.(phoneNumRegex) === null) {
      setInitname(value);
      setInitphone("");
    } else {
      setInitphone(value);
      setInitname("");
    }
    setCreateCustomerModal(true);
  };

  const handleAddCustomer = (payload) => {
    setCreateCustomerModal(false);
    setContactToUpdate(null);
    const c = (customers || []).find((x) => x.id === payload?.id) || payload;
    setCustomer(c);
    setState((e) => ({
      ...e,
      customerName: c?.name || "",
      customerPhone: c?.phone || "",
    }));
  };

  const selectedVoucherLabel = (() => {
    if (!state.voucherObj) return "";
    const effectivePrice = state.voucherObj.discounted_price ?? state.voucherObj.price;
    const name = state.voucherObj.voucher_type || "";
    return effectivePrice ? `${name} (Rs.${formatNum(effectivePrice)})` : name;
  })();

  return (
    <Box>
      {showPopularSection && (
        <Box className={bpStyles.popularSection}>
          <Box className={bpStyles.popularHeader}>
            <ParagraphText className={`fontSize14 newFontColor ${bpStyles.popularLabel}`}>
              {sentenceCase(I18n.popular)}
            </ParagraphText>
            {!showPopularSkeleton && (
              <ParagraphText
                id="sell_vouchers_see_more_link"
                className={bpStyles.seeMoreLink}
                onClick={() => {
                  triggerLogEvent(analytics, "w_earn_vouchers_see_more");
                  setShowAllVouchersModal(true);
                }}
              >
                {I18n.see_more}
              </ParagraphText>
            )}
          </Box>
          <Box className={bpStyles.popularRow}>
            {showPopularSkeleton
              ? [0, 1, 2].map((i) => (
                  <Box key={i} className={bpStyles.popularCardSkeleton} />
                ))
              : displayedPopular.map((voucher, i) => {
                  const displayName = voucher.voucher_type || "";
                  const icon = voucher?.vendor_icon || voucher.icon?.[0] || voucher.icon || "";
                  const iconKey = `popular_${voucher.voucher_id || i}`;
                  const isActive =
                    voucher.vendor_name === state.voucherCompany &&
                    voucher.voucher_id === state.voucherId;
                  return (
                    <Box
                      key={i}
                      id={`sell_vouchers_popular_card_${voucher.voucher_id || i}`}
                      className={`${bpStyles.popularCard} ${bpStyles[`popularCardBg${i}`] || ""} ${isActive ? bpStyles.popularCardActive : ""}`}
                      onClick={() => {
                        triggerLogEvent(analytics, "w_earn_vouchers_popular");
                        handlePopularVoucherSelect(voucher);
                      }}
                    >
                      {icon && !failedIcons[iconKey] ? (
                        <img
                          src={icon}
                          alt={displayName}
                          className={styles.popularIcon}
                          onError={() => markIconFailed(iconKey)}
                        />
                      ) : (
                        <VoucherPlaceholderIcon width={20} height={20} />
                      )}
                      <ParagraphText className={`fontSize14 ${bpStyles.popularName}`}>
                        {displayName}
                      </ParagraphText>
                    </Box>
                  );
                })}
          </Box>
        </Box>
      )}

      <Box className={bpStyles.reviewFields}>
        <Box className={`${formStyles.tabAutoCompleteBox} tabAutoCompleteBox`}>
          <TabAutoComplete
            id="voucher_company_select"
            list={companyOptions}
            label={I18n.voucher_company}
            handleSelect={(item) => {
              if (!item) {
                setState((e) => ({
                  ...e,
                  voucherCompany: "",
                  voucherCompanyId: "",
                  voucherId: "",
                  voucherObj: null,
                  // customerName: "",
                  // customerPhone: "",
                }));
                return;
              }
              if (item.name === state.voucherCompany) return;
              triggerLogEvent(analytics, "w_earn_vouchers_category");
              setState((e) => ({
                ...e,
                voucherCompany: item.name || item.value || "",
                voucherCompanyId: item.id || "",
                voucherId: "",
                voucherObj: null,
              }));
            }}
            value={state.voucherCompany || ""}
            placeholder={I18n.select_voucher_company}
            required
            handleStartLogEvent={() => {}}
            handleManuallyInput={() => {}}
            manuallyFocusInput={false}
          />
        </Box>

        {state.voucherCompany ? (
          <Box className={`${formStyles.tabAutoCompleteBox} tabAutoCompleteBox`}>
            <TabAutoComplete
              id="voucher_select"
              list={voucherOptions}
              label={I18n.voucher}
              handleSelect={(item) => {
                if (!item) {
                  setState((e) => ({
                    ...e,
                    voucherId: "",
                    voucherObj: null,
                    // customerName: "",
                    // customerPhone: "",
                  }));
                  return;
                }
                if (item.id === state.voucherId) return;
                triggerLogEvent(analytics, "w_earn_vouchers_select");
                setState((e) => ({
                  ...e,
                  voucherId: item.id || item.voucherObj?.voucher_id || "",
                  voucherObj: item.voucherObj || null,
                }));
              }}
              value={selectedVoucherLabel}
              placeholder={I18n.select_voucher}
              required
              handleStartLogEvent={() => {}}
              handleManuallyInput={() => {}}
              manuallyFocusInput={false}
            />
          </Box>
        ) : null}

        {state.voucherId ? (
          <Box className={`${formStyles.tabAutoCompleteBox} tabAutoCompleteBox`}>
            <TabAutoComplete
              id="voucher_contact"
              list={contactList}
              label={I18n.contact}
              handleSelect={(item) => {
                if (!item) {
                  setCustomer(undefined);
                  setState((e) => ({
                    ...e,
                    customerName: "",
                    customerPhone: "",
                  }));
                  return;
                }
                triggerLogEvent(analytics, "w_earn_vouchers_customer");
                const c = (customers || []).find((x) => x.id === item.id);
                if (c?.phone) {
                  setCustomer(c);
                  setState((e) => ({
                    ...e,
                    customerName: c?.name || item.labelText || "",
                    customerPhone: c?.phone || "",
                  }));
                } else {
                  setContactToUpdate(c);
                  showSnackbar({ message: I18n.please_add_phone_number_to_continue, severity: "info" });
                  setCreateCustomerModal(true);
                }
              }}
              value={state.customerName || ""}
              handleCreate={handleCreateContact}
              asCreate="contact"
              placeholder={I18n.select_a_contact}
              required
              handleStartLogEvent={() => {}}
              manuallyFocusInput={false}
              handleManuallyInput={() => {}}
            />
          </Box>
        ) : null}
      </Box>

      <SelectItemModal
        id="select_voucher_modal"
        open={showAllVouchersModal}
        onClose={() => setShowAllVouchersModal(false)}
        onSelect={handleModalVoucherSelect}
        title={I18n.select_voucher}
        items={allVouchersFlat}
        searchPlaceholder={I18n.search_voucher_by_name}
        noResultText={I18n.no_voucher_found}
      />

      <AddCustomerModal
        isOpen={createCustomerModal}
        handleClose={() => {
          setCreateCustomerModal(false);
          setContactToUpdate(null);
        }}
        navigate={navigate}
        initialName={initName}
        initialPhone={initPhone}
        onSaveCallback={handleAddCustomer}
        type="in"
        title={
          <ParagraphText className="ms-1 textLowercase">{I18n.customer}</ParagraphText>
        }
        isTypeField={false}
        handleLogEventCustomerCreated={() =>
          triggerLogEvent(analytics, "w_earn_vouchers_customer_created")
        }
        handleLogEventCustomerUpdate={() =>
          triggerLogEvent(analytics, "customer_data_updated")
        }
        isOnlyPhoneList={true}
        customerToUpdate={contactToUpdate}
      />
    </Box>
  );
};

export default VouchersPaymentForm;
